<template>
  <v-sheet
    class="py-4"
    elevation="2"
    rounded
    data-tourid="home-waitingForValidation"
  >
    <div class="px-6 mb-3 d-flex align-center justify-space-between">
      <p
        data-testid="home-text-validationsTitle"
        class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-3 font-weight-700"
      >
        {{ $t('home.validations.waiting_for_validation') }}
      </p>

      <Button
        v-if="revPages.length > 0"
        plain
        color="primary"
        size="small"
        data-testid="home-button-allValidations"
        :text="$t('rev.validation.go_to_validations')"
        :to="{ name: 'validations' }"
        @click="
          trackHomeNavigate({
            section: 'validations',
            element: 'goToValidations',
          })
        "
      />
    </div>

    <EmptyState
      v-if="!isLoading && revs.length === 0"
      :icon="'mdi-check-decagram-outline'"
      :title="$t('home.validations.empty_state_title')"
      :message="$t('home.validations.empty_state_message')"
    />

    <v-row v-if="isLoading" class="ma-0 pa-0 mx-3">
      <v-col
        v-for="item in 2"
        class="my-2"
        lg="6"
        md="12"
        :key="`skeleton-${item}`"
      >
        <ValidationCardSkeleton />
      </v-col>
    </v-row>

    <Carousel v-if="revPages.length > 0" height="186">
      <!-- On small screens we should generate the carousel's pages using individual Revs instead of chunks of 2 elements. -->
      <template v-if="$vuetify.breakpoint.mobile">
        <v-carousel-item v-for="rev in revs" :key="rev.id">
          <v-row class="mt-1 px-8" align="start" justify="start">
            <v-col lg="6" md="12">
              <ToDoItemCard :item="rev" pendingValidation />
            </v-col>
          </v-row>
        </v-carousel-item>
      </template>

      <!-- On big screens we should use chunks of 2 elements to generate the carousel's pages -->
      <template v-else>
        <v-carousel-item
          v-for="(page, index) in revPages"
          :key="`validationsPage-${index}`"
        >
          <v-row class="h-100 mt-0 pt-2 px-6" align="start" justify="start">
            <v-col v-for="rev in page" lg="6" md="12" :key="rev.id">
              <ToDoItemCard :item="rev" pendingValidation />
            </v-col>
          </v-row>
        </v-carousel-item>
      </template>
    </Carousel>
  </v-sheet>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button.vue'
import Carousel from '@/components/UI/Carousel.vue'

import ValidationCardSkeleton from './ValidationCardSkeleton.vue'
import EmptyState from '@/components/EmptyState.vue'
import ToDoItemCard from '../ToDo/ToDoItemCard.vue'

export default {
  name: 'Validations',

  mixins: [mixin, trackingMixin],

  components: {
    Button,
    Carousel,
    ToDoItemCard,
    EmptyState,
    ValidationCardSkeleton,
  },

  data() {
    return {
      isLoading: false,
      revs: [],
      revPages: [],
      totalRevs: 0,
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),
  },

  created() {
    this.fetchRevs()
  },

  methods: {
    async fetchRevs() {
      try {
        this.isLoading = true

        const response = await api.explore.getContent({
          organizationId: this.organizationId,
          limit: 8,
          filters: {
            types: [
              this.$constants.exploreEntityTypes.improvementIdea,
              this.$constants.exploreEntityTypes.problemSolving,
              this.$constants.exploreEntityTypes.quickAction,
            ],
            hasImplementationValidation: true,
            hasFinancialValidation: false,
            requestAssignees: [this.accountId],
            requestStatus: this.$constants.validationRequestStatus.inProgress,
          },
        })

        this.totalRevs = response.data.totalRecordsInData
        this.revs = response.data.results.map(result => result.response)
        this.revPages = _.chunk(this.revs, 2)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
